import { useState, useEffect, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, Download, ArrowLeft, Loader2, XCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("loading");
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const intervalRef = useRef(null);
  const maxAttempts = 30; // 60 seconds total (30 x 2s)

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }

    const checkStatus = async () => {
      try {
        const r = await axios.get(`${API}/checkout/status/${sessionId}`);
        const data = r.data;

        if (data.payment_status === "paid") {
          setStatus("success");
          setDownloadUrl(data.download_url);
          if (intervalRef.current) clearInterval(intervalRef.current);
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          if (intervalRef.current) clearInterval(intervalRef.current);
          return;
        }
      } catch (err) {
        // Don't stop on errors, keep polling
        console.log("Polling attempt failed, retrying...");
      }

      setAttempts(prev => {
        const next = prev + 1;
        if (next >= maxAttempts) {
          setStatus("error");
          if (intervalRef.current) clearInterval(intervalRef.current);
        }
        return next;
      });
    };

    // Initial check immediately
    checkStatus();

    // Poll every 2 seconds
    intervalRef.current = setInterval(checkStatus, 2000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [sessionId]); // Only depend on sessionId, not status/attempts

  const handleDownload = () => {
    if (downloadUrl) {
      // Use direct link instead of window.open (avoids popup blockers)
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  return (
    <div className="min-h-screen bg-navy-900 flex flex-col">
      <header className="bg-navy-900/80 backdrop-blur-lg border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <Link to="/" className="flex items-center gap-2 text-blue-200/70 hover:text-amber-400 transition-colors">
            <ArrowLeft className="w-5 h-5" /><span className="font-medium">Retour</span>
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-4">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-md">
          
          {/* Loading */}
          {status === "loading" && (
            <div className="card-dark text-center glow-blue" data-testid="payment-loading">
              <Loader2 className="w-16 h-16 mx-auto mb-6 text-amber-400 animate-spin" />
              <h1 className="font-display font-bold text-2xl text-white mb-3">Vérification du paiement</h1>
              <p className="text-blue-200/60 mb-6">Merci de patienter, cela peut prendre quelques secondes...</p>
              <div className="bg-white/5 rounded-lg p-3">
                <div className="w-full bg-white/10 rounded-full h-2 mb-2">
                  <div className="bg-amber-500 h-2 rounded-full transition-all duration-500" style={{ width: `${Math.min((attempts / maxAttempts) * 100, 100)}%` }}></div>
                </div>
                <p className="text-xs text-blue-300/50">Vérification en cours...</p>
              </div>
            </div>
          )}

          {/* Success */}
          {status === "success" && (
            <div className="card-dark text-center glow-gold" data-testid="payment-success">
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring" }}
                className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-emerald-400" />
              </motion.div>
              <h1 className="font-display font-bold text-2xl text-white mb-3">Paiement réussi!</h1>
              <p className="text-blue-200/60 mb-6">Merci pour ton achat! Ton guide est prêt.</p>
              
              {/* Primary download button */}
              <button data-testid="download-pdf-button" onClick={handleDownload}
                className="btn-gold w-full flex items-center justify-center gap-2 text-lg py-4 mb-3">
                <Download className="w-6 h-6" /> Télécharger le PDF
              </button>

              {/* Direct link fallback */}
              {downloadUrl && (
                <a href={downloadUrl} target="_blank" rel="noopener noreferrer"
                  className="block w-full text-center bg-white/5 text-blue-200/70 hover:text-white font-medium py-3 rounded-xl border border-white/10 hover:border-white/20 transition-all text-sm mb-4">
                  Lien direct de téléchargement (si le bouton ne fonctionne pas)
                </a>
              )}

              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 mt-4">
                <p className="text-sm text-amber-300/70"><strong>Problème de téléchargement?</strong> Contacte-nous à excelltutorat@outlook.com avec ta confirmation de paiement.</p>
              </div>

              <Link to="/" className="inline-flex items-center gap-2 mt-6 text-blue-300/50 hover:text-amber-400 transition-colors text-sm">
                <ArrowLeft className="w-4 h-4" /> Retour à l'accueil
              </Link>
            </div>
          )}

          {/* Error */}
          {status === "error" && (
            <div className="card-dark text-center" data-testid="payment-error">
              <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-red-400" />
              </div>
              <h1 className="font-display font-bold text-2xl text-white mb-3">Vérification en cours</h1>
              <p className="text-blue-200/60 mb-4">Nous n'avons pas pu confirmer ton paiement automatiquement.</p>
              <p className="text-blue-200/60 mb-8 text-sm">Si tu as été débité, ne t'inquiète pas! Envoie un email à <strong className="text-amber-400">excelltutorat@outlook.com</strong> avec ta confirmation et nous t'enverrons ton guide.</p>
              <Link to="/" data-testid="retry-button" className="btn-gold inline-flex items-center gap-2"><ArrowLeft className="w-5 h-5" /> Retour</Link>
            </div>
          )}

          {/* Expired */}
          {status === "expired" && (
            <div className="card-dark text-center" data-testid="payment-expired">
              <div className="w-20 h-20 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-amber-400" />
              </div>
              <h1 className="font-display font-bold text-2xl text-white mb-3">Session expirée</h1>
              <p className="text-blue-200/60 mb-8">Réessaie pour compléter ton achat.</p>
              <Link to="/" data-testid="retry-expired-button" className="btn-gold inline-flex items-center gap-2">Réessayer</Link>
            </div>
          )}
        </motion.div>
      </main>

      <footer className="bg-navy-900 border-t border-white/5 py-6">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-blue-300/40 text-xs">© 2026 Excell-Tutorat</p>
        </div>
      </footer>
    </div>
  );
};

export default SuccessPage;
